
import { google } from 'google-maps';
import PathMap from '@/components/FieldAndPath/PathMap.vue';
import Component from 'vue-class-component';
import { getModule } from 'vuex-module-decorators';
import PathStore from '../store/modules/paths';
import FieldStore from '../store/modules/fields';
import { Mixins, Watch } from 'vue-property-decorator';
import { SimplePath } from '@/flightpath-components/models/SimplePath';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { Guid } from 'guid-typescript';
import { FieldOperationsModel } from '@/flightpath-components/models/FieldOperationsModel';
import { SimplePoint } from '@/flightpath-components/models/SimplePoint';
import MqttMixIn from '@/mixins/mqttManagement';
import NotificationMixIn from '@/mixins/notifications';
import { PubSub } from '@/PubSubClass';
import { Convert as GenericNotifyConvert } from '@/flightpath-components/models/mqttPayload/GenericNotificationMessage';
import { NotificationMessageType } from '@/flightpath-components/models/NotificationMessageType';
import LastModifiedMixIn from '@/mixins/lastModified';
import { AltitudeFinder } from '@/utils/AltitudeFinder';
import { PathHelper } from '@/utils/PathHelper';
import { GPSLocation } from '@/flightpath-components/models/GPSLocation';
import GlobalStore from '@/store/modules/global';

// Define the component in class-style
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({ name: 'Paths', inject: ['container'], components: { PathMap } })
export default class Paths extends Mixins(MqttMixIn, NotificationMixIn, LastModifiedMixIn) {
  public pathStore = getModule(PathStore, this.$store);
  public fieldStore = getModule(FieldStore, this.$store);
  public globalStore = getModule(GlobalStore, this.$store);
  type = [NotificationMessageType.NewPath, NotificationMessageType.ModifiedPath];
  panel = [0];
  editMode = false;
  pathSelected = false;
  addPathMode = false;
  editWorkAreaMode = false;
  showWorkAreaControls = false;
  editCropAreaMode = false;
  showCropAreaControls = false;
  emptyPath: SimplePath = new SimplePath();
  selectedPath = this.emptyPath;
  selectedPathLength = 0;
  selectedWorkAreaArea = 0;
  selectedCropAreaArea = 0;
  emptyField: FieldOperationsModel = new FieldOperationsModel();

  selectedField = this.emptyField;
  newPathName = '';
  tempLine: google.maps.LatLng[] = [];
  tempWorkArea: google.maps.LatLng[] = [];
  tempCropAreas: google.maps.LatLng[][] = [];
  deleteWorkAreaDialog = false;
  deleteCropAreaDialog = false;
  deletePathDialog = false;
  deleting = false;

  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  private container!: Container;

  async validateUpload(file: File, fieldId: string) {
    try {
      const fileContents = await file.text();
      if (fileContents == undefined) return;
      const result = JSON.parse(fileContents) as SimplePath;
      if (result.Path.length == 0) {
        console.error('path has no points');
      }
      if (result.Name == null || result.Name == '') {
        result.Name = `path_upload_${new Date().toDateString()}`;
      }

      // convert to GPS location to remove duplicate points
      const path = result.Path.map((value) => {
        return { Latitude: value.Lat, Longitude: value.Long, Elevation: value.Alt } as GPSLocation;
      });
      const distinctPath = PathHelper.removeDuplicateSuccessivePoints(path);

      // convert back to simple points
      const distinctSimplePath = distinctPath.map((value) => {
        return { Lat: value.Latitude, Long: value.Longitude, Alt: value.Elevation } as SimplePoint;
      });

      const newPath = new SimplePath();
      newPath.PathID = Guid.create().toString();
      newPath.CustomerID = this.globalStore.accountId;
      newPath.Name = result.Name;
      newPath.Path = distinctSimplePath;
      newPath.WorkAreas = result.WorkAreas;
      newPath.CropAreas = result.CropAreas;
      newPath.FieldID = fieldId;
      newPath.PathLengthMeters = 0;
      newPath.StrokeOpacity = 0.5;
      newPath.StrokeWeight = 1;
      newPath.LastModifiedUTC = new Date();
      await this.flightPathService?.insertOrUpdatePath(newPath);
      return newPath;
    } catch (error) {
      console.error(error);
    } finally {
      await this.getCurrentPaths();
    }
  }

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
    this.addMqttVisibility(this.globalStore.generalNotificationTopic, () =>
      PubSub.subscribe(this.globalStore.generalNotificationTopic).subscribe({
        next: (data) => this.notificationRecieved(data),
        error: (error) => this.mqttError(error)
      })
    );
  }

  async mounted() {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();

    this.actions.forEach((act, key) => {
      this.subscriptions.set(key, act());
    });
    this.emptyField.FieldName = this.$t('none').toString();
  }

  destroyed() {
    if (this.pathStore.edit) this.pathStore.toggleEditable();
    this.removeMqttVisibility();
  }

  private async notificationRecieved(data: any): Promise<void> {
    const response = data.value as string;
    const status = GenericNotifyConvert.toGenericNotification(JSON.stringify(response));

    if (status.Type == NotificationMessageType.NewPath) {
      const entityResponse = await this.flightPathService?.getPathByPathId(status.EntityId, this.globalStore.accountId);
      const entity = JSON.parse(JSON.stringify(entityResponse?.data)) as SimplePath;

      const index = this.pathStore.availablePaths.findIndex((value) => value.PathID == entity.PathID);
      if (index < 0) {
        this.pathStore.paths.push(entity);
      } else {
        this.pathStore.paths.splice(index, 1, entity);
      }
    }
  }

  @Watch('pathStore.editable')
  onEditableChange(): void {
    this.editMode = this.pathStore.editable;
  }

  @Watch('pathStore.selectedPathLengthInMeters')
  onSelectedPathLengthChange(length: number): void {
    this.selectedPathLength = length;
  }

  @Watch('pathStore.selectedWorkAreaAreaInSqMeters')
  onSelectedWorkAreaAreaChange(area: number): void {
    this.selectedWorkAreaArea = this.pathStore.workAreaAreaHectares;
  }

  @Watch('pathStore.editWorkArea')
  onEditWorkAreaChange(value: boolean) {
    this.editWorkAreaMode = value;
  }

  @Watch('pathStore.editCropArea')
  onEditCropAreaChange(value: boolean) {
    this.editCropAreaMode = value;
  }

  @Watch('selectedField')
  onSelectedFieldChange() {
    this.pathStore.setSelectedField(this.selectedField);
  }

  get pathLengthDisplay() {
    return !this.pathSelected ? '' : `${this.selectedPathLength}m`;
  }
  get fieldNameDisplay() {
    return !this.pathSelected ? '' : this.selectedField.FieldName;
  }
  get lastEditDisplay() {
    return this.pathSelected && this.selectedPath.LastModifiedUTC != this.emptyPath.LastModifiedUTC ? this.formattedDate : '';
  }
  get formattedDate() {
    const date = new Date(this.selectedPath.LastModifiedUTC);
    return date.toLocaleDateString();
  }
  get workAreaDisplay() {
    let area = this.$t('undefined');
    if (this.pathSelected && this.selectedWorkAreaArea > 0) {
      area = `${this.selectedWorkAreaArea}`;
    }
    return !this.pathSelected ? '' : area;
  }
  get workAreaDefined() {
    return this.pathSelected && this.showWorkAreaControls;
  }
  get cropAreaDisplay() {
    let area = this.$t('undefined');
    if (this.pathSelected && this.selectedCropAreaArea > 0) {
      area = `${this.selectedCropAreaArea}`;
    }
    return !this.pathSelected ? '' : area;
  }
  get cropAreaDefined() {
    return this.pathSelected && this.showCropAreaControls;
  }
  get editingGeometry() {
    return this.pathStore.editLine || this.editWorkAreaMode || this.editCropAreaMode;
  }


  get fields() {
    return this.fieldStore.availableFields;
  }

  async getCurrentPaths() {
    if (!this.flightPathService) return;
    const storedPaths = await this.flightPathService.getPathsByCustomerId(this.globalStore.accountId);
    this.pathStore.setAvailablePaths(SimplePath.toModelCollection(JSON.stringify(storedPaths.data)));
  }

  async selectPath(path: SimplePath) {
    if (path.PathID == this.selectedPath.PathID) return;
    if (!this.flightPathService) return;
    if (this.pathStore.edit) return;
    try {
      const response = await this.flightPathService.getPathByPathId(path.PathID, this.globalStore.accountId);
      const simplePath = SimplePath.toModel(JSON.stringify(response.data));
      this.pathSelected = true;
      this.selectedPath = simplePath;
      this.pathStore.setSelectedPath(simplePath);
      this.updateSelected(simplePath.PathID);
      // handle paths that do not have a field associated with them
      const fieldIndex = this.fields.findIndex((element) => element.FieldID == this.selectedPath.FieldID);
      if (fieldIndex == -1) {
        this.selectedField = new FieldOperationsModel();
      } else {
        this.selectedField = this.fields[fieldIndex];
      }

      this.pathStore.setSelectedField(this.selectedField);
    } catch (error) {
      console.error(error);
    }
  }

  editPath() {
    if (this.pathSelected) {
      this.pathStore.toggleEditable();
      this.newPathName = this.selectedPath.Name;
      this.showWorkAreaControls = this.pathStore.currentWorkArea.length > 0;
      this.showCropAreaControls = this.pathStore.cropAreas.length > 0;
    }
  }

  cancelEdit() {
    const fieldIndex = this.fields.findIndex((element) => element.FieldID == this.selectedPath.FieldID);
    if (fieldIndex == -1) {
      this.selectedField = new FieldOperationsModel();
    } else {
      this.selectedField = this.fields[fieldIndex];
    }

    this.pathStore.toggleEditable();
    this.pathStore.setCurrentLine(this.selectedPath.Path.map((value) => new window.google.maps.LatLng(value.Lat, value.Long)));
    this.pathStore.triggerRevertPath();
    this.pathStore.setCurrentWorkArea(this.selectedPath.WorkAreas[0].map((value) => new window.google.maps.LatLng(value.Lat, value.Long)));
    this.pathStore.triggerRevertWorkArea();
    this.pathStore.setCurrentCropAreas(this.selectedPath.CropAreas.map((area) => area.map((value) => new window.google.maps.LatLng(value.Lat, value.Long))));
    this.pathStore.triggerRevertCropArea();
    this.addPathMode = false;
  }

  newPath() {
    this.pathStore.toggleEditable();
    this.clearPathInfo();
    this.addPathMode = true;
    this.pathStore.setAddPathMode(this.addPathMode);
  }

  cancelNewPath() {
    this.pathSelected = false;
    this.pathStore.toggleEditable();
    this.addPathMode = false;
    this.pathStore.setAddPathMode(this.addPathMode);
    this.clearPathInfo();
  }

  clearPathInfo() {
    this.pathStore.triggerClearMap();
    this.pathStore.setSelectedPathLength(0);
    this.selectedField = new FieldOperationsModel();
    this.selectedPath = new SimplePath();
    this.newPathName = '';
  }

  validateNewPathInfo() {
    return this.newPathName.length != 0 && this.pathStore.currentPath.length != 0;
  }

  async savePath() {
    if (!this.flightPathService) return;

    if (this.addPathMode) {
      if (!this.validateNewPathInfo()) {
        alert(this.$t('views.paths.alert.validation'));
        return;
      }
      this.selectedPath.Name = this.newPathName;
      this.selectedPath.CustomerID = this.globalStore.accountId;
      this.selectedPath.PathID = Guid.create().toString();
    } else {
      this.selectedPath.Name = this.newPathName;
      this.editPath();
    }

    const newWorkArea: SimplePoint[] = this.pathStore.workArea.map((value) => ({ Lat: value.lat(), Long: value.lng(), Alt: 0 }));
    if (this.selectedPath.WorkAreas == undefined) {
      this.selectedPath.WorkAreas = [];
    }

    if (this.selectedPath.WorkAreas.length == 0) {
      this.selectedPath.WorkAreas.push(newWorkArea);
    } else {
      this.selectedPath.WorkAreas[0] = newWorkArea;
    }

    const cropAreas: SimplePoint[][] = this.pathStore.cropAreas.map((area) => area.map((value) => ({ Lat: value.lat(), Long: value.lng(), Alt: 0 })));
    this.selectedPath.CropAreas = cropAreas.length > 0 ? cropAreas : [[]];

    // must convert SimplePath[] to GPSLocation[] to remove the duplicate points, and then back to SimplePath[]
    this.selectedPath.Path = PathHelper.removeDuplicateSuccessivePoints(
      this.selectedPath.Path.map((value) => {
        return { Latitude: value.Lat, Longitude: value.Long, Elevation: value.Alt } as GPSLocation;
      })
    ).map((value) => {
      return { Lat: value.Latitude, Long: value.Longitude, Alt: value.Elevation } as SimplePoint;
    });

    // take the points from googlemap with are in 2d, and find the closest known altitude for each one using the source 3d data set
    this.selectedPath.Path = AltitudeFinder.Find(
      this.selectedPath.Path,
      this.pathStore.currentPath.map((val) => ({ Lat: val.lat(), Long: val.lng(), Alt: 0 }))
    );
    this.selectedPath.FieldID = this.selectedField.FieldID;

    const finalPath = SimplePath.toModel(this.selectedPath.fromModelToJson());

    if (this.addPathMode) this.cancelNewPath();

    await this.flightPathService.insertOrUpdatePath(finalPath);
    await this.getCurrentPaths();
  }

  async deletePath() {
    if (!this.flightPathService) return;

    const path = this.selectedPath;
    this.deleting = true;
    try {
      await this.flightPathService.deletePathByPathId(this.selectedPath.PathID, this.globalStore.accountId);
      await this.getCurrentPaths();
    } finally {
      this.deleting = false;
    }
    this.pathStore.triggerClearMap();
    this.selectedPath = new SimplePath();
    this.pathSelected = false;
    this.selectedPathLength = 0;
    this.deletePathDialog = false;
    this.$emit('deleted', path);
  }

  cancelGeometryEdits() {
    if (this.pathStore.editLine) this.cancelEditLine();
    if (this.pathStore.editWorkArea) this.cancelEditWorkArea();
    if (this.pathStore.editCropArea) this.cancelEditCropArea();
  }

  editLine() {
    this.cancelGeometryEdits();
    this.tempLine = this.pathStore.currentPath.map((value) => new window.google.maps.LatLng(value.lat(), value.lng()));
    this.pathStore.setEditLine(true);
  }

  cancelEditLine() {
    this.pathStore.setCurrentLine(this.tempLine);
    this.pathStore.triggerRevertPath();
    this.pathStore.setEditLine(false);
  }

  editWorkArea(edit: boolean) {
    if (edit) this.cancelGeometryEdits();
    this.tempWorkArea = this.pathStore.workArea.map((value) => new window.google.maps.LatLng(value.lat(), value.lng()));
    this.showWorkAreaControls = edit;
    this.pathStore.setEditWorkArea(edit);
  }

  cancelEditWorkArea() {
    this.pathStore.setCurrentWorkArea(this.tempWorkArea);
    this.pathStore.triggerRevertWorkArea();
    this.pathStore.setEditWorkArea(false);
    if (this.tempWorkArea.length == 0) this.showWorkAreaControls = false;
  }

  saveWorkArea() {
    if (this.pathStore.workArea.length > 0) {
      this.showWorkAreaControls = true;
    }

    this.pathStore.setEditWorkArea(false);
  }

  deleteWorkArea() {
    this.showWorkAreaControls = false;
    this.pathStore.triggerWorkAreaDelete();
    this.pathStore.setCurrentWorkArea([]);
    this.deleteWorkAreaDialog = false;
  }

  editCropArea(edit: boolean) {
    if (edit) this.cancelGeometryEdits();
    this.tempCropAreas = this.pathStore.cropAreas.map((area) => area.map((value) => new window.google.maps.LatLng(value.lat(), value.lng())));
    this.showCropAreaControls = edit;
    this.pathStore.setEditCropArea(edit);
  }

  cancelEditCropArea() {
    this.pathStore.setCurrentCropAreas(this.tempCropAreas);
    this.pathStore.triggerRevertCropArea();
    this.pathStore.setEditCropArea(false);
    if (this.tempCropAreas.length == 0) this.showCropAreaControls = false;
  }

  saveCropArea() {
    if (this.pathStore.cropAreas.length > 0) {
      this.showCropAreaControls = true;
    }

    this.pathStore.setEditCropArea(false);
  }

  deleteCropArea() {
    this.showCropAreaControls = false;
    this.pathStore.triggerCropAreaDelete();
    this.deleteCropAreaDialog = false;
  }
}
